import React from 'react';
import lodashStartsWith from 'lodash/startsWith';
import lodashEndsWith from 'lodash/endsWith';
import { Editor, getEventRange, getEventTransfer } from 'slate-react';
import { Block, Value } from 'slate';
import { Button, Row, Col } from 'react-bootstrap';
import { Translate } from 'react-redux-i18n';
import PropTypes from 'prop-types';
import Html from 'slate-html-serializer';
import imageExtensions from 'image-extensions';
import { LAST_CHILD_TYPE_INVALID, CHILD_TYPE_INVALID } from 'slate-schema-violations';
import isUrl from 'is-url';
import NotificationSystem from 'react-notification-system';

import SpinnerButton from './SpinnerButton';
import { isSY } from '../../actions/root_url';
/*
  Example Usage:
  <RichTextEditor
    closeRichTextModal={() => { this.closeMobileDescBox(); }}
    saveHtml={(htmlText) => { this.saveRichText(htmlText); }}
    htmlString={mobileDescriptionHTML}
    placeholder="Enter the product description."
    saveSuccess={this.props.saveMobileDescriptionTextSuccess}
  />
*/

const propTypes = {
  attributes: PropTypes.shape({
    'data-key': PropTypes.string,
  }),
  children: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    type: PropTypes.string,
    props: PropTypes.object,
  })),
  node: PropTypes.shape({}),
  mark: PropTypes.shape({}),
  isSelected: PropTypes.bool,
  htmlString: PropTypes.string,
  placeholder: PropTypes.string,
  isLoadingSave: PropTypes.bool,
  saveSuccess: PropTypes.bool,
  closeRichTextModal: PropTypes.func,
  saveHtml: PropTypes.func,
};

const defaultProps = {
  attributes: {},
  children: {},
  node: {},
  mark: {},
  isSelected: false,
  htmlString: '',
  placeholder: '',
  isLoadingSave: false,
  saveSuccess: null,
  closeRichTextModal: () => {},
  saveHtml: () => {},
};

/*
 * A function to determine whether a URL has an image extension.
 *
 * @param {String} url
 * @return {Boolean}
 */

function isImage(url) {
  return !!imageExtensions.find(url.endsWith);
}

/**
 * A change function to standardize inserting images.
 *
 * @param {Change} change
 * @param {String} src
 * @param {Range} target
 */

function insertImage(change, src, target) {
  if (target) {
    change.select(target);
  }

  change.insertBlock({
    type: 'image',
    isVoid: true,
    data: { src },
  });
}

function insertLink(change, title, link, node) {
  if(node) {
    change.removeNodeByKey(node.key);
  }
  change.insertBlock({
    type: 'link',
    isVoid: true,
    data: {
      href: link,
      title
    },
  });
}

function isIframe(str) {
  return lodashStartsWith(str, '<iframe') && lodashEndsWith(str, '</iframe>');
}

const blockTags = {
  p: 'paragraph',
  ul: 'bulleted-list',
  li: 'list-item',
  ol: 'numbered-list',
  img: 'image',
  iframe: 'video',
  a: 'link'
};

const markTags = {
  em: 'italic',
  strong: 'bold',
  u: 'underlined',
};

const schema = {
  document: {
    nodes: [{ types: ['paragraph', 'bulleted-list', 'list-item', 'numbered-list', 'image', 'link'] }],
    last: { types: ['paragraph'] },
    normalize: (change, reason, context) => {
      switch (reason) {
        case LAST_CHILD_TYPE_INVALID: {
          const paragraph = Block.create('paragraph');
          return change.insertNodeByKey(context.node.key, context.node.nodes.size, paragraph);
        }
        case CHILD_TYPE_INVALID: {
          const src = context.child.data.get('src');
          const paragraph = Block.create({
            object: 'block',
            type: 'paragraph',
            nodes: [
              {
                object: 'text',
                leaves: [
                  {
                    text: `<iframe title="video" style={{ width: '100vw', height: '56.25vw' }} src="${src}" frameBorder="0" allow="autoplay; encrypted-media" allowFullScreen></iframe>`,
                  },
                ],
              },
            ],
          });
          return change.replaceNodeByKey(context.child.key, paragraph);
        }
        default:
          return null;
      }
    },
  },
};

const rules = [
  {
    deserialize(el, next) {
      const type = blockTags[el.tagName.toLowerCase()];
      if (!type) {
        return undefined;
      }
      if (type === 'video') {
        const src = el.getAttribute('src');
        return {
          object: 'block',
          type,
          data: {
            src,
          },
          isVoid: true,
          nodes: next(el.chlidNodes),
        };
      }
      if (type === 'image') {
        return {
          object: 'block',
          type,
          data: {
            src: el.getAttribute('src'),
            style: el.getAttribute('style'),
            alt: el.getAttribute('alt'),
            className: null,
          },
          isVoid: true,
          nodes: next(el.childNodes),
        };
      }
      if (type === 'link') {
        return {
          object: 'block',
          type,
          data: {
            href: el.getAttribute('href'),
            title: el.text,
            style: el.getAttribute('style')
          }
        }
      }
      return {
        object: 'block',
        type,
        nodes: next(el.childNodes),
      };
    },
    serialize(obj, children) {
      if (obj.object !== 'block') {
        return undefined;
      }
      switch (obj.type) {
        case 'paragraph': {
          if (isIframe(obj.text)) {
            const video = obj.text.split(' ');
            let src;
            video.forEach((part) => {
              if (lodashStartsWith(part, 'src')) {
                src = part.substr(5);
                src = src.substr(0, src.length - 1);
              }
            });
            const videoIframe = (
              <iframe
                title="video"
                style={{ width: '100vw', height: '56.25vw' }}
                src={src}
                frameBorder="0"
                allow="autoplay; encrypted-media"
                allowFullScreen
              />
            );
            return videoIframe;
          }
          return <p style={{ maxWidth: '100%' }}>{ children }</p>;
        }
        case 'bulleted-list':
          return <ul style={{ maxWidth: '100%' }}>{ children }</ul>;
        case 'list-item':
          return <li style={{ maxWidth: '100%' }}>{ children }</li>;
        case 'numbered-list':
          return <ol style={{ maxWidth: '100%' }}>{ children }</ol>;
        case 'image': {
          const src = obj.data.get('src');
          const style = {
            display: 'block',
            maxWidth: '100%',
            // maxHeight: '20em',
          };
          return (
            <img src={src} alt="" style={style} />
          );
        }
        case 'link': 
          const href = obj.data.get('href');
          const title = obj.data.get('title');
          return (
            <a href={href}>{title}</a>
          )
        default:
          return undefined;
      }
    },
  },
  {
    deserialize(el, next) {
      const type = markTags[el.tagName.toLowerCase()];
      if (!type) {
        return undefined;
      }
      return {
        object: 'mark',
        type,
        nodes: next(el.childNodes),
      };
    },
    serialize(obj, children) {
      if (obj.object !== 'mark') {
        return undefined;
      }
      switch (obj.type) {
        case 'bold':
          return <strong>{ children }</strong>;
        case 'italic':
          return <em>{ children }</em>;
        case 'underlined':
          return <u>{ children }</u>;
        default:
          return undefined;
      }
    },
  },
];

const initialValue = Value.fromJSON({
  document: {
    nodes: [
      {
        object: 'block',
        type: 'paragraph',
        nodes: [
          {
            object: 'text',
            leaves: [
              {
                text: '',
              },
            ],
          },
        ],
      },
    ],
  },
});

const html = new Html({ rules });

/**
 * Define the default node type.
 *
 * @type {String}
 */

const DEFAULT_NODE = 'paragraph';

/**
 * The rich text example.
 *
 * @type {Component}
 */

class RichTextEditor extends React.Component {
  /**
   * Deserialize the initial editor value.
   *
   * @type {Object}
   */
  constructor(props) {
    super(props);
    this.state = {
      value: Value.fromJSON(initialValue),
      linkModalVisibility: false,
      currentNode: null
    };
  }

  componentWillMount() {
    const { htmlString } = this.props;
    if (htmlString.length > 0) {
      this.setInitialText();
    }
    this._notificationSystem = this.refs.notificationSystem;
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.saveSuccess && nextProps.saveSuccess !== this.props.saveSuccess) {
      this.handleAddNotification('save_successful', 'success');
    }
  }

  /**
   * On change, save the new `value`.
   *
   * @param {Change} change
   */

  onChange = ({ value }) => {
    this.setState({ value });
  }

  /**
   * When a mark button is clicked, toggle the current mark.
   *
   * @param {Event} event
   * @param {String} type
   */

  onClickMark = (event, type) => {
    event.preventDefault();
    const { value } = this.state;
    const change = value.change().toggleMark(type);
    this.onChange(change);
  }

  /**
   * When a block button is clicked, toggle the block type.
   *
   * @param {Event} event
   * @param {String} type
   */

  onClickBlock = (event, type) => {
    event.preventDefault();
    const { value } = this.state;
    const change = value.change();
    const { document } = value;

    // Handle everything but list buttons.
    if (type !== 'bulleted-list' && type !== 'numbered-list') {
      const isActive = this.hasBlock(type);
      const isList = this.hasBlock('list-item');

      if (isList) {
        change
          .setBlocks(isActive ? DEFAULT_NODE : type)
          .unwrapBlock('bulleted-list')
          .unwrapBlock('numbered-list');
      } else {
        change.setBlocks(isActive ? DEFAULT_NODE : type);
      }
    } else {
      // Handle the extra wrapping required for list buttons.
      const isList = this.hasBlock('list-item');
      const isType = value.blocks.some(block => (
        !!document.getClosest(block.key, parent => parent.type === type)
      ));

      if (isList && isType) {
        change
          .setBlocks(DEFAULT_NODE)
          .unwrapBlock('bulleted-list')
          .unwrapBlock('numbered-list');
      } else if (isList) {
        change
          .unwrapBlock(type === 'bulleted-list'
            ? 'numbered-list'
            : 'bulleted-list')
          .wrapBlock(type);
      } else {
        change.setBlocks('list-item').wrapBlock(type);
      }
    }

    this.onChange(change);
  }

  onClickImage = (event) => {
    event.preventDefault();
    const file = event.target.files[0];
    if (!file) {
      return;
    }
    let src;
    const reader = new FileReader();
    reader.onload = () => {
      src = reader.result;
      const change = this.state.value.change().call(insertImage, src);
      this.onChange(change);
    };
    const blob = file.slice(0, file.size, file.type);
    reader.readAsDataURL(blob);
  }

  /**
   * On drop, insert the image wherever it is dropped.
   *
   * @param {Event} event
   * @param {Change} change
   * @param {Editor} editor
   */

  onDropOrPaste = (event, change, editor) => {
    const target = getEventRange(event, change.value);
    if (!target && event.type === 'drop') {
      return;
    }
    const transfer = getEventTransfer(event);
    const { type, text, files } = transfer;

    if (type === 'files') {
      files.forEach((file) => {
        const reader = new FileReader();
        const [mime] = file.type.split('/');
        if (mime === 'image') {
          reader.addEventListener('load', () => {
            editor.change((c) => {
              c.call(insertImage, reader.result, target);
            });
          });
          reader.readAsDataURL(file);
        }
      });
    }

    if (type === 'text') {
      if (!isUrl(text)) {
        return;
      }
      if (!isImage(text)) {
        return;
      }
      change.call(insertImage, text, target);
    }
  }

  setInitialText = () => {
    const { htmlString } = this.props;
    const value = html.deserialize(htmlString);
    this.setState({ value });
  }

  _notificationSystem = null;

  /**
   * Check if the any of the currently selected blocks are of `type`.
   *
   * @param {String} type
   * @return {Boolean}
   */

  hasBlock = (type) => {
    const { value } = this.state;
    return value.blocks.some(node => node.type === type);
  }

  /**
   * Check if the current selection has a mark with `type` in it.
   *
   * @param {String} type
   * @return {Boolean}
   */

  hasMark = (type) => {
    const { value } = this.state;
    return value.activeMarks.some(mark => mark.type === type);
  }

  handleSave = () => {
    const serializedValue = html.serialize(this.state.value);
    this.props.saveHtml(serializedValue);
  }

  handleAddNotification = (title, level) => {
    this.refs.notificationSystem.addNotification({
      title: <Translate value={title} />,
      level,
    });
  }
  
  handleToggleLinkModalVisibility = () => {
    this.setState({
      linkModalVisibility: !this.state.linkModalVisibility,
      currentNode: {title: '', link: ''}
    })
  }
  
  handleLinkModalTitleChange = ({target}) => {
    this.setState({
      currentNode: {...this.state.currentNode, title: target.value}
    })
  } 
  
  handleLinkModalLinkChange = ({target}) => {
    this.setState({
      currentNode: {...this.state.currentNode, link: target.value}
    })
  }
  
  handleInsertALink = () => {
    if(this.state.currentNode) {
      const change = this.state.value.change().call(insertLink, this.state.currentNode.title, this.state.currentNode.link, this.state.currentNode.node);
      this.onChange(change);
      this.setState({
        currentNode: null,
        linkModalVisibility: false
      })
    }
  }
  
  handleClickLink = (event, node) => {
    this.setState({
      currentNode: {node, title: node.data.get('title'), link: node.data.get('href')},
      linkModalVisibility: true
    })
  }

  /**
   * Render the toolbar.
   *
   * @return {Element}
   */

  renderToolbar = () => {
    return (
      <div className="menu toolbar-menu">
        { this.renderMarkButton('bold', 'fa fa-bold') }
        { this.renderMarkButton('italic', 'fa fa-italic') }
        { this.renderMarkButton('underlined', 'fa fa-underline') }
        { this.renderBlockButton('numbered-list', 'fa fa-list-ol') }
        { this.renderBlockButton('bulleted-list', 'fa fa-list') }
        { this.renderImageButton('fa fa-image') }
        { this.renderLinkButton('fa fa-link') }
        { this.renderSaveButton() }
      </div>
    );
  }
  
  renderSaveButton = () => {
    const className = isSY ? 'save-button-with-link' : 'save-button'
    return <SpinnerButton
      bsStyle="success"
      className={className}
      onClick={this.handleSave}
      disabled={this.props.isLoadingSave}
      loading={this.props.isLoadingSave}
      icon="fa-save"
      translation={<Translate value="save" />}
    />
  }
  
  renderLinkButton = icon => isSY && (
    <div className="link-button-wrapper">
      {this.state.linkModalVisibility && <div className="link-modal">
        <div className="link-modal-title">
          <Translate value="insert_link"/>
          <em className="fa fa-close" onMouseDown={this.handleToggleLinkModalVisibility}/>
        </div>
        <Row>
          <Col xs={12}>
            <label><Translate value="title"/></label>
            <input value={this.state.currentNode ? this.state.currentNode.title : ''} onChange={this.handleLinkModalTitleChange}/>
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <label><Translate value="link"/></label>
            <input value={this.state.currentNode ? this.state.currentNode.link : ''} onChange={this.handleLinkModalLinkChange}/>
          </Col>
        </Row>
        <Row>
          <Col xs={12} className="button-wrapper">
            <Button className="insert-button" onMouseDown={this.handleInsertALink}><Translate value="insert"/></Button>
          </Col>
        </Row>
      </div> }
      <Button className="link-button" onMouseDown={this.handleToggleLinkModalVisibility}>
        <em className={icon} />
      </Button>
    </div>
  )

  renderImageButton = icon => (
    <div className="image-button-wrapper">
      <input
        id="myInput"
        type="file"
        ref={(ref) => { this.upload = ref; }}
        style={{ display: 'none' }}
        onChange={this.onClickImage}
      />
      <Button className="image-button" onMouseDown={() => { this.upload.click(); }}>
        <em className={icon} />
      </Button>
    </div>
  )

  /**
   * Render a mark-toggling toolbar button.
   *
   * @param {String} type
   * @param {String} icon
   * @return {Element}
   */

  renderMarkButton = (type, icon) => {
    const isActive = this.hasMark(type);
    const onMouseDown = event => this.onClickMark(event, type);

    return (
      <Button className="format-buttons" onMouseDown={onMouseDown} data-active={isActive}>
        <em className={icon} />
      </Button>
    );
  }

  /**
   * Render a block-toggling toolbar button.
   *
   * @param {String} type
   * @param {String} icon
   * @return {Element}
   */

  renderBlockButton = (type, icon) => {
    const isActive = this.hasBlock(type);
    const onMouseDown = event => this.onClickBlock(event, type);

    return (
      <Button className="format-buttons" onMouseDown={onMouseDown} data-active={isActive}>
        <em className={icon} />
      </Button>
    );
  }

  /**
   * Render the Slate editor.
   *
   * @return {Element}
   */

  renderEditor = () => (
    <div className="editor">
      <Editor
        placeholder={this.props.placeholder}
        value={this.state.value}
        onChange={this.onChange}
        onDrop={this.onDropOrPaste}
        onPaste={this.onDropOrPaste}
        renderNode={this.renderNode}
        renderMark={this.renderMark}
        schema={schema}
        spellCheck
      />
    </div>
  );

  /**
   * Render a Slate node.
   *
   * @param {Object} props
   * @return {Element}
   */

  renderNode = (props) => {
    const {
      attributes,
      children,
      node,
      isSelected,
    } = props;
    switch (node.type) {
      case 'bulleted-list':
        return <ul {...attributes}>{ children }</ul>;
      case 'list-item':
        return <li {...attributes}>{ children }</li>;
      case 'numbered-list':
        return <ol {...attributes}>{ children }</ol>;
      case 'image': {
        const src = node.data.get('src');
        const className = isSelected ? 'active' : null;
        const style = { display: 'block' };
        return (
          <img src={src} alt="" className={className} style={style} {...attributes} />
        );
      }
      case 'video': {
        return <iframe title="video" />;
      }
      case 'link': {
        const href = node.data.get('href');
        const title = node.data.get('title');
        return <a href={href} {...attributes} onClick={event => {
          event.preventDefault();
          this.handleClickLink(event, node);
        }}>{title}</a>
      }
      default:
        return null;
    }
  }

  /**
   * Render a Slate mark.
   *
   * @param {Object} props
   * @return {Element}
   */

  renderMark = (props) => {
    const { children, mark } = props;
    switch (mark.type) {
      case 'bold':
        return <strong>{ children }</strong>;
      case 'italic':
        return <em>{ children }</em>;
      case 'underlined':
        return <u>{ children }</u>;
      default:
        return null;
    }
  }

  /**
   * Render.
   *
   * @return {Element}
   */

  render() {
    const style = {
      NotificationItem: { // Override the notification item
        DefaultStyle: { // Applied to every notification, regardless of the notification level
          margin: '10px 5px 2px 1px',
        },
        success: { // Applied only to the success notification item
          color: 'red',
        },
      },
    };
    return (
      <div id="rich-text-editor">
        { this.renderToolbar() }
        { this.renderEditor() }
        <NotificationSystem ref="notificationSystem" style={style} />
      </div>
    );
  }
}

RichTextEditor.propTypes = propTypes;
RichTextEditor.defaultProps = defaultProps;

export default RichTextEditor;



// WEBPACK FOOTER //
// ./master/jsx/components/Common/RichTextEditor.jsx